import { Volume2 } from "lucide-react";
import { useT } from "@/i18n";
import { LANGS, GREETINGS, PHRASES, VOCAB, ALPHABET, EXTRA_LETTERS, LANG_TEXT } from "@/components/languages/content";
import useMascotSpeak from "@/lib/useMascotSpeak";

const voiceOf = (id) => LANGS.find((l) => l.id === id).voice;

const useSay = (target, mascot) => {
  const say = useMascotSpeak();
  return (text) => say(text, target, mascot, voiceOf(target));
};

const WordCard = ({ item, target, ui, testId, speak }) => (
  <button type="button" onClick={() => speak(item[target])} className="bg-white rounded-3xl p-4 shadow-md text-left hover:-translate-y-1 transition-transform flex items-start gap-3" data-testid={testId}>
    <Volume2 className="w-5 h-5 text-blue-600 shrink-0 mt-1" />
    <span>
      <span className="block font-fredoka text-xl font-bold text-blue-950">{item[target]}</span>
      {ui !== target && <span className="block text-sm font-semibold text-blue-900/70">{item[ui]}</span>}
    </span>
  </button>
);

export const Greetings = ({ target, mascot }) => {
  const { lang } = useT();
  const speak = useSay(target, mascot);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="lang-greetings">
      {GREETINGS.map((g, i) => <WordCard key={i} item={g} target={target} ui={lang} testId={`greet-${i}`} speak={speak} />)}
    </div>
  );
};

export const Phrases = ({ target, mascot }) => {
  const { lang } = useT();
  const speak = useSay(target, mascot);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lang-phrases">
      {PHRASES.map((p, i) => <WordCard key={i} item={p} target={target} ui={lang} testId={`phrase-${i}`} speak={speak} />)}
    </div>
  );
};

export const Alphabet = ({ target, mascot }) => {
  const { lang } = useT();
  const speak = useSay(target, mascot);
  const extras = EXTRA_LETTERS[target];
  return (
    <div data-testid="lang-alphabet">
      <div className="grid grid-cols-6 sm:grid-cols-9 lg:grid-cols-[repeat(13,minmax(0,1fr))] gap-2">
        {ALPHABET.map((l, i) => (
          <button key={l} type="button" onClick={() => speak(l)} className={`aspect-square rounded-2xl border-4 border-white shadow font-fredoka font-bold text-2xl sm:text-3xl text-white hover:scale-110 transition-transform ${["bg-pink-500", "bg-sky-500", "bg-yellow-400", "bg-green-500", "bg-violet-500", "bg-orange-500"][i % 6]}`} data-testid={`letter-${l}`}>
            {l}
          </button>
        ))}
      </div>
      {extras.length > 0 && (
        <div className="mt-4">
          <p className="font-bold text-blue-950">{LANG_TEXT[lang].extras}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {extras.map((l) => (
              <button key={l} type="button" onClick={() => speak(l)} className="w-14 h-14 rounded-2xl bg-white border-4 border-yellow-300 shadow font-fredoka font-bold text-2xl text-blue-950 hover:scale-110 transition-transform" data-testid={`letter-extra-${l}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Vocab = ({ target, mascot }) => {
  const { lang } = useT();
  const speak = useSay(target, mascot);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="lang-vocab">
      {VOCAB.map((v) => (
        <button key={v.id} type="button" onClick={() => speak(v[target])} className="bg-white rounded-3xl p-3 shadow-md hover:-translate-y-1 transition-transform flex flex-col items-center" data-testid={`vocab-${v.id}`}>
          <img src={`/img/vocab/${v.id}.webp`} alt={v.en} className="h-20 sm:h-24 w-auto object-contain" />
          <span className="mt-2 font-fredoka text-xl font-bold text-blue-950 inline-flex items-center gap-1"><Volume2 className="w-4 h-4 text-blue-600" />{v[target]}</span>
          {lang !== target && <span className="text-sm font-semibold text-blue-900/70">{v[lang]}</span>}
        </button>
      ))}
    </div>
  );
};
